import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/apiClient";
import { PageHeader } from "../components/Layout";
import { toast } from "sonner";
import { PlusCircle, Save, Trash2, Pencil, X, Info, Layers } from "lucide-react";

const CATEGORIES = ["Baza", "Başlık", "Yatak", "Komodin", "Bench"];

export default function PieceRulesPage() {
  const [rules, setRules] = useState([]);
  const [sizes, setSizes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // {id?, category, model, size, pieces_per_product}
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [rr, sr] = await Promise.all([api.get("/piece-rules"), api.get("/sizes")]);
      setRules(rr.data);
      setSizes(sr.data);
    } catch (e) { toast.error(formatApiError(e)); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const startNew = () => setEditing({ category: "Baza", model: "*", size: "", pieces_per_product: 2 });
  const startEdit = (r) => setEditing({ ...r, size: r.size || "", model: r.model || "*" });
  const cancel = () => setEditing(null);

  const save = async () => {
    if (!editing) return;
    const isBaza = editing.category === "Baza";
    const body = {
      category: editing.category,
      model: isBaza ? "*" : ((editing.model || "").trim() || "*"),
      size: isBaza ? (editing.size || "").trim() : "",
      pieces_per_product: Math.max(1, Math.min(20, parseInt(editing.pieces_per_product || "1"))),
    };
    setSaving(true);
    try {
      if (editing.id) {
        await api.put(`/piece-rules/${editing.id}`, body);
        toast.success("Kural güncellendi");
      } else {
        await api.post("/piece-rules", body);
        toast.success("Kural eklendi");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/piece-rules/${deleteTarget.id}`);
      toast.success("Kural silindi");
      setDeleteTarget(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // Group by category for display
  const grouped = CATEGORIES.map(c => ({ cat: c, items: rules.filter(r => r.category === c) }));

  return (
    <div>
      <PageHeader
        title="Parça Kuralları"
        subtitle="Kural bazlı otomatik parça sayısı. Baza için ÖLÇÜ, diğer kategoriler için MODEL üzerinden karar verilir."
        actions={
          <button onClick={startNew} data-testid="rule-new-btn"
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-700 hover:bg-blue-800 text-white font-bold text-sm uppercase">
            <PlusCircle size={16}/> Yeni Kural
          </button>
        }
      />

      <div className="mb-4 p-3 border-l-4 border-blue-500 bg-blue-50 text-blue-900 text-sm flex items-start gap-2">
        <Info size={18} className="mt-0.5 shrink-0"/>
        <div>
          <b>Baza</b> için <b>Ölçü</b>, diğer kategoriler için <b>Model</b> alanı kullanılır.
          Boş bırakırsanız (<code className="font-mono px-1 bg-white">*</code>) kategoride
          <b> tüm modeller / tüm ölçüler için varsayılan</b> olur. Belirli bir ölçü/model için satır varsa
          o satır önceliklidir.
        </div>
      </div>

      {loading && <div className="text-slate-500">Yükleniyor...</div>}
      {!loading && (
        <div className="space-y-4">
          {grouped.map(({ cat, items }) => {
            const isBaza = cat === "Baza";
            const headerLabel = isBaza ? "Ölçü" : "Model";
            return (
              <section key={cat} className="card-industrial">
                <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-2">
                  <Layers size={16} className="text-slate-600"/>
                  <span className="font-display font-black text-slate-900 text-base">{cat}</span>
                  <span className="text-xs text-slate-500">({items.length} kural)</span>
                </div>
                {items.length === 0 ? (
                  <div className="px-4 py-6 text-sm text-slate-400 italic">Bu kategori için henüz kural yok — varsayılan 1 parça uygulanır.</div>
                ) : (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-slate-50 border-b border-slate-200 text-[11px] uppercase tracking-widest text-slate-600">
                        <th className="text-left px-4 py-2 font-bold w-1/2">{headerLabel}</th>
                        <th className="text-left px-4 py-2 font-bold">Parça / Ürün</th>
                        <th className="text-right px-4 py-2 font-bold">İşlem</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((r, i) => {
                        const displayValue = isBaza ? (r.size || "") : (r.model || "");
                        const isWildcard = isBaza ? !r.size : (r.model === "*");
                        return (
                          <tr key={r.id} data-testid={`rule-row-${r.category}-${displayValue || "wildcard"}`}
                            className={`border-b border-slate-100 ${i % 2 === 0 ? "bg-white" : "bg-slate-50"}`}>
                            <td className="px-4 py-2 font-mono">
                              {isWildcard ? (
                                <span className="inline-flex items-center gap-1">
                                  <code className="px-1.5 py-0.5 bg-amber-100 text-amber-900 border border-amber-400 font-bold text-xs">*</code>
                                  <span className="text-slate-500 italic">{isBaza ? "Tüm ölçüler (varsayılan)" : "Tüm modeller (varsayılan)"}</span>
                                </span>
                              ) : (
                                <span className="font-bold text-slate-900">{displayValue}</span>
                              )}
                            </td>
                            <td className="px-4 py-2 font-display font-black text-blue-700 text-lg tabular-nums">{r.pieces_per_product}</td>
                            <td className="px-4 py-2 text-right">
                              <button onClick={() => startEdit(r)} data-testid={`rule-edit-${r.id}`}
                                className="inline-flex items-center gap-1 text-blue-700 hover:text-blue-900 text-xs font-bold mr-3">
                                <Pencil size={13}/> Düzenle
                              </button>
                              <button onClick={() => setDeleteTarget(r)} data-testid={`rule-delete-${r.id}`}
                                className="inline-flex items-center gap-1 text-red-700 hover:text-red-900 text-xs font-bold">
                                <Trash2 size={13}/> Sil
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </section>
            );
          })}
        </div>
      )}

      {/* Edit modal */}
      {editing && (
        <div className="fixed inset-0 bg-black/50 z-50 grid place-items-center p-4" onClick={cancel}>
          <div className="bg-white max-w-md w-full p-5 shadow-xl border-2 border-slate-800" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display font-black text-lg">
                {editing.id ? "Kuralı Düzenle" : "Yeni Kural"}
              </h3>
              <button onClick={cancel} className="p-1 hover:bg-slate-100"><X size={18}/></button>
            </div>

            <div className="space-y-3">
              <div>
                <label className="text-[11px] uppercase tracking-widest font-bold text-slate-600">Kategori</label>
                <select
                  value={editing.category}
                  onChange={(e) => setEditing({ ...editing, category: e.target.value })}
                  data-testid="rule-form-category"
                  className="w-full mt-1 px-3 py-2 border-2 border-slate-800 focus:border-blue-700 focus:outline-none font-bold"
                >
                  {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>

              {editing.category === "Baza" ? (
                <div>
                  <label className="text-[11px] uppercase tracking-widest font-bold text-slate-600">
                    Ölçü <span className="text-slate-400 font-normal normal-case">(boş = tüm ölçüler için varsayılan)</span>
                  </label>
                  <select
                    value={editing.size || ""}
                    onChange={(e) => setEditing({ ...editing, size: e.target.value })}
                    data-testid="rule-form-size"
                    className="w-full mt-1 px-3 py-2 border-2 border-slate-800 focus:border-blue-700 focus:outline-none font-mono"
                    autoFocus
                  >
                    <option value="">— Tüm Ölçüler (varsayılan) —</option>
                    {sizes.map(s => <option key={s.id} value={s.name}>{s.name}</option>)}
                    {editing.size && !sizes.find(s => s.name === editing.size) && (
                      <option value={editing.size}>{editing.size} (özel)</option>
                    )}
                  </select>
                </div>
              ) : (
                <div>
                  <label className="text-[11px] uppercase tracking-widest font-bold text-slate-600">
                    Model <span className="text-slate-400 font-normal normal-case">(* = tüm modeller)</span>
                  </label>
                  <input
                    value={editing.model}
                    onChange={(e) => setEditing({ ...editing, model: e.target.value })}
                    placeholder="Örn: Capella, Alfa veya *"
                    data-testid="rule-form-model"
                    className="w-full mt-1 px-3 py-2 border-2 border-slate-800 focus:border-blue-700 focus:outline-none font-mono"
                    autoFocus
                  />
                </div>
              )}

              <div>
                <label className="text-[11px] uppercase tracking-widest font-bold text-slate-600">Parça Sayısı (1–20)</label>
                <input
                  type="number"
                  min="1"
                  max="20"
                  value={editing.pieces_per_product}
                  onChange={(e) => setEditing({ ...editing, pieces_per_product: e.target.value })}
                  data-testid="rule-form-pieces"
                  className="w-full mt-1 px-3 py-2 border-2 border-slate-800 focus:border-blue-700 focus:outline-none font-mono font-bold text-lg"
                />
              </div>
            </div>

            <div className="mt-5 flex justify-end gap-2">
              <button onClick={cancel} data-testid="rule-form-cancel"
                className="px-4 py-2 border border-slate-300 hover:bg-slate-100 text-sm font-bold uppercase">
                Vazgeç
              </button>
              <button onClick={save} disabled={saving} data-testid="rule-form-save"
                className="inline-flex items-center gap-2 px-5 py-2 bg-blue-700 hover:bg-blue-800 text-white font-bold text-sm uppercase disabled:opacity-50">
                <Save size={16}/> {saving ? "Kaydediliyor..." : "Kaydet"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirm */}
      {deleteTarget && (
        <div className="fixed inset-0 bg-black/50 z-50 grid place-items-center p-4" onClick={() => setDeleteTarget(null)}>
          <div className="bg-white max-w-md w-full p-5 shadow-xl border-2 border-red-600" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display font-black text-lg text-red-800 mb-2">Kuralı Sil</h3>
            <p className="text-sm text-slate-700 mb-4">
              <b>{deleteTarget.category} · {
                deleteTarget.category === "Baza"
                  ? (deleteTarget.size || "Tüm ölçüler")
                  : (deleteTarget.model === "*" ? "Tüm modeller" : deleteTarget.model)
              }</b> için
              <b> {deleteTarget.pieces_per_product} parça</b> kuralı silinecek. Devam edilsin mi?
            </p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setDeleteTarget(null)} className="px-4 py-2 border border-slate-300 hover:bg-slate-100 text-sm font-bold uppercase">
                Vazgeç
              </button>
              <button onClick={doDelete} data-testid="rule-delete-confirm"
                className="inline-flex items-center gap-2 px-5 py-2 bg-red-700 hover:bg-red-800 text-white font-bold text-sm uppercase">
                <Trash2 size={16}/> Evet, Sil
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
